import { soundService } from '../services/soundService';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Play, CheckCircle2, ChevronRight, Clock, Award, 
  Flame, Target, Sparkles, AlertCircle, RotateCcw, 
  Calendar, BookOpen, ChevronDown, Plus, Globe, Check,
  Settings, X, Loader2, Trash2, RotateCw, MoreHorizontal
} from 'lucide-react';
import { StudyPlan, studyPlanService, DailyPlanProgress, PlanTask } from '../services/studyPlanService';
import { errorReviewService } from '../services/errorReviewService';
import { motion, AnimatePresence } from 'framer-motion';
import { HomeOrbOverview, HomePanel } from './HomeOrbOverview';
import { getLocalISOString } from '../utils/timezoneUtils';
import T from './T';
import i18nInstance from '../i18n';


interface StudyPlanHomeDashboardProps {
  userId: string | undefined;
  activePlan: StudyPlan | null;
  onOpenNewPlanModal: () => void;
  onOpenRoadmapModal: () => void;
  onSelectLanguage: (lang: string) => void;
  availableLangs: string[];
}

const LANGUAGE_CONFIG: Record<string, { name: string; flag: string }> = {
  en: { name: 'Inglês', flag: '🇺🇸' },
  fr: { name: 'Francês', flag: '🇫🇷' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  es: { name: 'Espanhol', flag: '🇪🇸' },
  de: { name: 'Alemão', flag: '🇩🇪' },
  ru: { name: 'Russo', flag: '🇷🇺' },
  hu: { name: 'Húngaro', flag: '🇭🇺' },
  pt: { name: 'Português', flag: '🇧🇷' }
};

export const StudyPlanHomeDashboard: React.FC<StudyPlanHomeDashboardProps> = ({
  userId,
  activePlan,
  onOpenNewPlanModal,
  onOpenRoadmapModal,
  onSelectLanguage,
  availableLangs
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [dailyProgress, setDailyProgress] = useState<DailyPlanProgress | null>(null);
  const [pendingErrors, setPendingErrors] = useState<any[]>([]);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [masteredErrors, setMasteredErrors] = useState<any[]>([]);
  const [errorTab, setErrorTab] = useState<'pending' | 'mastered'>('pending');
  const [summaryPeriod, setSummaryPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');
  const [reportData, setReportData] = useState<any>(null);
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState<boolean>(false);
  const [deckImportNotice, setDeckImportNotice] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    targetUrl: string;
  } | null>(null);
  const [isImportingDeck, setIsImportingDeck] = useState(false);
  const [localPlan, setLocalPlan] = useState<StudyPlan | null>(activePlan);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [customMinutes, setCustomMinutes] = useState<number>(activePlan?.daily_goal_minutes || 30);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isDeletingPlan, setIsDeletingPlan] = useState(false);
  const [userPlanLangs, setUserPlanLangs] = useState<string[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(true);
  const [isDayDoneModalOpen, setIsDayDoneModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Fecha o menu de 3 pontinhos ao clicar fora
  useEffect(() => {
    if (!isMoreMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) setIsMoreMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isMoreMenuOpen]);
  // Design minimal v1: qual orbe da visão geral está aberto (null = todos recolhidos)
  const [openPanel, setOpenPanel] = useState<HomePanel | null>(null);
  // Ao abrir um orbe, rola suavemente até a janela detalhada
  useEffect(() => {
    if (!openPanel) return;
    const timer = setTimeout(() => {
      document.getElementById('home-orb-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(timer);
  }, [openPanel]);

  // Idioma nativo da interface para internacionalização dos modais
  const nativeLang = (i18n.language || (typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : 'pt') || 'pt').split('-')[0].toLowerCase();

  const deleteModalI18n = useMemo(() => {
    const dict: Record<string, {
      title: string;
      warning: string;
      instruction: string;
      placeholder: string;
      confirm: string;
      cancel: string;
      deleting: string;
    }> = {
      pt: {
        title: 'Tem certeza que deseja excluir sua rota de aprendizado?',
        warning: 'Todo o progresso, dias concluídos e histórico deste idioma serão apagados. Você poderá criar um novo plano do zero quando quiser.',
        instruction: 'Digite "delete" na caixa abaixo para desbloquear a confirmação:',
        placeholder: 'Digite delete para confirmar',
        confirm: 'Confirmar',
        cancel: 'Cancelar',
        deleting: 'Excluindo...'
      },
      en: {
        title: 'Are you sure you want to delete your learning route?',
        warning: 'All progress, completed days, and history for this language will be erased. You can create a new plan from scratch anytime.',
        instruction: 'Type "delete" in the box below to unlock confirmation:',
        placeholder: 'Type delete to confirm',
        confirm: 'Confirm',
        cancel: 'Cancel',
        deleting: 'Deleting...'
      },
      fr: {
        title: 'Êtes-vous sûr de vouloir supprimer votre parcours d\'apprentissage ?',
        warning: 'Tous les progrès, jours terminés et historique pour cette langue seront effacés. Vous pourrez créer un nouveau plan à zéro quand vous voudrez.',
        instruction: 'Tapez "delete" dans la boîte ci-dessous pour débloquer la confirmation :',
        placeholder: 'Tapez delete pour confirmer',
        confirm: 'Confirmer',
        cancel: 'Annuler',
        deleting: 'Suppression...'
      },
      es: {
        title: '¿Estás seguro de que deseas eliminar tu ruta de aprendizaje?',
        warning: 'Todo el progreso, días completados e historial de este idioma serán eliminados. Podrás crear un nuevo plan desde cero cuando desees.',
        instruction: 'Escribe "delete" en el cuadro de abajo para desbloquear la confirmación:',
        placeholder: 'Escribe delete para confirmar',
        confirm: 'Confirmar',
        cancel: 'Cancelar',
        deleting: 'Eliminando...'
      },
      it: {
        title: 'Sei sicuro di voler eliminare il tuo percorso di apprendimento?',
        warning: 'Tutti i progressi, i giorni completati e la cronologia di questa lingua verranno cancellati. Potrai creare un nuovo piano da zero quando vorrai.',
        instruction: 'Digita "delete" nella casella sottostante per sbloccare la conferma:',
        placeholder: 'Digita delete per confermare',
        confirm: 'Conferma',
        cancel: 'Annulla',
        deleting: 'Eliminazione...'
      },
      de: {
        title: 'Möchten Sie Ihren Lernpfad wirklich löschen?',
        warning: 'Der gesamte Fortschritt, abgeschlossene Tage und der Verlauf dieser Sprache werden gelöscht. Sie können jederzeit einen neuen Plan erstellen.',
        instruction: 'Geben Sie "delete" in das untenstehende Feld ein, um die Bestätigung freizuschalten:',
        placeholder: 'Geben Sie delete ein zur Bestätigung',
        confirm: 'Bestätigen',
        cancel: 'Abbrechen',
        deleting: 'Löschen...'
      },
      ja: {
        title: '学習ルートを削除してもよろしいですか？',
        warning: 'この言語のすべての進捗状況、完了した日数、履歴が削除されます。いつでも最初から新しいプランを作成できます。',
        instruction: '確認を解除するには、下のボックスに「delete」と入力してください：',
        placeholder: '確認のため delete と入力',
        confirm: '確認',
        cancel: 'キャンセル',
        deleting: '削除中...'
      },
      zh: {
        title: '您确定要删除您的学习路线吗？',
        warning: '该语言的所有进度、已完成天数和历史记录都将被清除。您可以随时从头开始创建新计划。',
        instruction: '请在下方输入框中输入 "delete" 以确认删除：',
        placeholder: '输入 delete 以确认',
        confirm: '确认',
        cancel: '取消',
        deleting: '正在删除...'
      },
      ru: {
        title: 'Вы уверены, что хотите удалить свой план обучения?',
        warning: 'Весь прогресс, завершенные дни и история для этого языка будут удалены. Вы сможете создать новый план с нуля в любое время.',
        instruction: 'Введите "delete" в поле ниже, чтобы разблокировать подтверждение:',
        placeholder: 'Введите delete для подтверждения',
        confirm: 'Подтвердить',
        cancel: 'Отмена',
        deleting: 'Удаление...'
      },
      hu: {
        title: 'Biztosan törölni szeretné a tanulási útvonalát?',
        warning: 'Minden előrehaladás, befejezett nap és előzmény törlődik ehhez a nyelvhez. Bármikor új tervet hozhat létre a semmiből.',
        instruction: 'Írja be a "delete" szót az alábbi mezőbe a megerősítés feloldásához:',
        placeholder: 'Írja be: delete a megerősítéshez',
        confirm: 'Megerősítés',
        cancel: 'Mégse',
        deleting: 'Törlés...'
      }
    };

    return dict[nativeLang] || dict.pt;
  }, [nativeLang]);

  useEffect(() => {
    setLocalPlan(activePlan);
    if (activePlan?.daily_goal_minutes) {
      setCustomMinutes(activePlan.daily_goal_minutes);
    }
  }, [activePlan]);

  const refreshUserPlans = useCallback(async () => {
    try {
      const plans = await studyPlanService.getUserPlans(userId);
      const langs = plans.map(p => p.language_code.toLowerCase().split('-')[0]);
      if (activePlan?.language_code) {
        const cur = activePlan.language_code.toLowerCase().split('-')[0];
        if (!langs.includes(cur)) langs.push(cur);
      }
      setUserPlanLangs(langs.length > 0 ? Array.from(new Set(langs)) : (activePlan ? [activePlan.language_code.toLowerCase().split('-')[0]] : ['en']));
    } catch (e) {
      if (activePlan) {
        setUserPlanLangs([activePlan.language_code.toLowerCase().split('-')[0]]);
      }
    }
  }, [userId, activePlan]);

  useEffect(() => {
    refreshUserPlans();
  }, [refreshUserPlans]);

  useEffect(() => {
    const handlePlanEvent = () => refreshUserPlans();
    window.addEventListener('study_plan_updated', handlePlanEvent);
    return () => window.removeEventListener('study_plan_updated', handlePlanEvent);
  }, [refreshUserPlans]);

  const displayPlan = localPlan || activePlan;
  const cleanLang = (displayPlan?.language_code || 'en').toLowerCase().split('-')[0];
  const langMeta = LANGUAGE_CONFIG[cleanLang] || { name: cleanLang.toUpperCase(), flag: '🌐' };

  // 1. Carregar Erros e Maestria
  const loadErrors = useCallback(async () => {
    if (!userId || userId === 'guest') return;
    try {
      const pending = await errorReviewService.getPendingErrors(userId, cleanLang);
      setPendingErrors(pending || []);
    } catch (e) {}
  }, [userId, cleanLang]);

  // Salvar novo tempo diário e recalcular tarefas em aberto (mantendo tarefas concluídas intactas)
  const handleSaveDailyMinutes = async () => {
    if (!displayPlan || !userId) return;
    setIsSavingSettings(true);

    try {
      const newMinutes = Math.max(5, customMinutes);

      // 1. Recalcular projeções do plano
      const projections = studyPlanService.calculateProjections(
        displayPlan.current_level,
        newMinutes,
        new Date(displayPlan.start_date || new Date())
      );

      const updatedPlan: StudyPlan = {
        ...displayPlan,
        daily_goal_minutes: newMinutes,
        projected_a2_date: projections.a2Date,
        projected_b1_date: projections.b1Date,
        projected_b2_date: projections.b2Date,
        projected_c1_date: projections.c1Date,
        projected_c2_date: projections.c2Date
      };

      await studyPlanService.savePlan(updatedPlan);
      setLocalPlan(updatedPlan);

      // 2. Recalcular tarefas abertas do dia
      if (dailyProgress) {
        const freshTasks = studyPlanService.generateTasksForDay({
          dayIndex: displayPlan.current_day_index,
          level: displayPlan.current_level,
          langCode: cleanLang,
          dailyMinutes: newMinutes,
          pendingErrors
        });

        const freshByType = new Map<string, PlanTask>();
        freshTasks.forEach(ft => freshByType.set(ft.type, ft));

        const updatedTasks = dailyProgress.tasks.map(task => {
          // Tarefas já concluídas NÃO DEVEM SER ALTERADAS
          if (task.completed) {
            return task;
          }

          // Tarefas em aberto recalculadas proporcionalmente
          const fresh = freshByType.get(task.type);
          if (fresh) {
            return {
              ...task,
              estimated_minutes: fresh.estimated_minutes,
              target_seconds: fresh.target_seconds,
              target_count: fresh.target_count,
              title: fresh.title,
              description: fresh.description,
              target_url: fresh.target_url
            };
          }
          return task;
        });

        // Incluir novos tipos de tarefas introduzidos pelo aumento de tempo se houver
        freshTasks.forEach(ft => {
          const alreadyExists = updatedTasks.some(ut => ut.type === ft.type);
          if (!alreadyExists) {
            updatedTasks.push(ft);
          }
        });

        const updatedProgress: DailyPlanProgress = {
          ...dailyProgress,
          tasks: updatedTasks,
          total_tasks_count: updatedTasks.length
        };

        await studyPlanService.saveDailyProgress(userId, cleanLang, updatedProgress);
        setDailyProgress(updatedProgress);
      }

      window.dispatchEvent(new CustomEvent('study_plan_updated', {
        detail: { lang: cleanLang, plan: updatedPlan }
      }));

      setIsSettingsOpen(false);
    } catch (err) {
      console.error('[StudyPlanHomeDashboard] Erro ao salvar novo tempo:', err);
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Excluir rota de aprendizado do idioma selecionado
  const handleDeletePlan = async () => {
    if (!displayPlan || deleteConfirmationText.trim().toLowerCase() !== 'delete') return;
    setIsDeletingPlan(true);

    try {
      await studyPlanService.deletePlan(userId, cleanLang);
      setLocalPlan(null);
      setDailyProgress(null);

      // Obter planos remanescentes e selecionar outro idioma se existir
      const remainingPlans = await studyPlanService.getUserPlans(userId);
      const remainingLangs = remainingPlans
        .map(p => p.language_code.toLowerCase().split('-')[0])
        .filter(l => l !== cleanLang);

      if (remainingLangs.length > 0) {
        onSelectLanguage(remainingLangs[0]);
      }

      // Notificar a aplicação que o plano deste idioma foi removido
      window.dispatchEvent(new CustomEvent('study_plan_updated', {
        detail: { lang: cleanLang, plan: null }
      }));

      setIsDeleteModalOpen(false);
      setDeleteConfirmationText('');
    } catch (err) {
      console.error('[StudyPlanHomeDashboard] Erro ao excluir rota de estudos:', err);
    } finally {
      setIsDeletingPlan(false);
    }
  };

  // 2. Carregar Tarefas do Dia Atual (planArg: plano recém-lido, usado pela sincronização manual)
  const loadDailyTasks = useCallback(async (planArg?: StudyPlan): Promise<DailyPlanProgress | null> => {
    const plan = planArg || activePlan;
    if (!plan) {
      setIsLoadingTasks(false);
      return null;
    }
    setIsLoadingTasks(true);
    try {
      const todayStr = getLocalISOString().split('T')[0];
      let prog = await studyPlanService.getDailyProgress(userId, cleanLang, plan.current_day_index);

      if (!prog) {
        // Gerar tarefas do dia APENAS se ainda não existirem para este dia de estudos
        const freshTasks = studyPlanService.generateTasksForDay({
          dayIndex: plan.current_day_index,
          level: plan.current_level,
          langCode: cleanLang,
          dailyMinutes: plan.daily_goal_minutes,
          pendingErrors
        });

        prog = {
          plan_day: plan.current_day_index,
          schedule_date: todayStr,
          tasks: freshTasks,
          completed_tasks_count: 0,
          total_tasks_count: freshTasks.length,
          minutes_spent: 0,
          status: 'pending'
        };

        await studyPlanService.saveDailyProgress(userId, cleanLang, prog);
      }

      // Hidratar o progresso parcial das tarefas preservando tarefas já salvas e contadores
      if (prog && prog.tasks) {
        prog.tasks.forEach(t => {
          if (!t.completed) {
            const keyDayTime = `libritui_task_time_${userId}_${cleanLang}_${plan.current_day_index}_${t.id}`;
            const keyDayItems = `libritui_task_items_${userId}_${cleanLang}_${plan.current_day_index}_${t.id}`;
            const keyLegacyTime = `libritui_task_time_${userId}_${t.id}_${todayStr}`;
            const keyLegacyItems = `libritui_task_items_${userId}_${t.id}_${todayStr}`;

            const savedItems = parseInt(
              localStorage.getItem(keyDayItems) || localStorage.getItem(keyLegacyItems) || '0',
              10
            );
            const savedTime = parseInt(
              localStorage.getItem(keyDayTime) || localStorage.getItem(keyLegacyTime) || '0',
              10
            );

            if (t.target_count && (savedItems > 0 || (t.current_progress && t.current_progress > 0))) {
              t.current_progress = Math.max(t.current_progress || 0, savedItems);
            } else if (t.target_seconds && (savedTime > 0 || (t.current_progress && t.current_progress > 0))) {
              t.current_progress = Math.max(t.current_progress || 0, savedTime);
            }
          }
        });

        // Tarefa que já bateu a meta mas ficou sem o "concluída" (ex.: saiu da página antes do evento)
        let changed = false;
        prog.tasks.forEach(t => {
          const target = t.target_count || t.target_seconds || 0;
          if (!t.completed && target > 0 && (t.current_progress || 0) >= target) {
            t.completed = true;
            changed = true;
          }
        });
        const doneCount = prog.tasks.filter(t => t.completed).length;
        if (doneCount !== prog.completed_tasks_count || prog.total_tasks_count !== prog.tasks.length) changed = true;
        if (changed) {
          prog.completed_tasks_count = doneCount;
          prog.total_tasks_count = prog.tasks.length;
          prog.status = doneCount >= prog.tasks.length ? 'completed' : doneCount > 0 ? 'in_progress' : 'pending';
          await studyPlanService.saveDailyProgress(userId, cleanLang, prog);
        }
      }

      setDailyProgress(prog);
      return prog;
    } catch (e) {
      console.warn('Erro ao carregar tarefas diárias:', e);
      return null;
    } finally {
      setIsLoadingTasks(false);
    }
  }, [userId, cleanLang, activePlan, pendingErrors]);

  // 3. Carregar Relatórios & Equilíbrio de Habilidades
  const loadAnalytics = useCallback(async () => {
    if (!userId || userId === 'guest') return;
    setIsLoadingReport(true);
    try {
      const res = await fetch('/api/db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_analytics_period_report',
          user_id: userId,
          data: {
            period: summaryPeriod,
            target_lang: cleanLang,
            local_date: getLocalISOString().split('T')[0]
          }
        })
      });
      const json = await res.json();
      if (json.success) {
        setReportData(json);
        if (json.mastered_errors) setMasteredErrors(json.mastered_errors);
        if (json.pending_errors) setPendingErrors(json.pending_errors);
      }
    } catch (e) {
    } finally {
      setIsLoadingReport(false);
    }
  }, [userId, cleanLang, summaryPeriod]);

  useEffect(() => {
    loadErrors();
    loadAnalytics();
  }, [loadErrors, loadAnalytics]);

  useEffect(() => {
    loadDailyTasks();
  }, [loadDailyTasks]);

  // Ao voltar para a Home a página recebe foco de novo: recarrega o estado das missões
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadDailyTasks(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadDailyTasks]);

  // Listener para tarefas concluídas (ex: audiobook target reached)
  useEffect(() => {
    const handleTaskCompleted = (e: any) => {
      const detail = e.detail;
      if (dailyProgress && detail?.type) {
        const matchingTask = dailyProgress.tasks.find(t => t.type === detail.type && !t.completed);
        if (matchingTask) {
          handleToggleTask(matchingTask.id, true);
        }
      }
    };

    const handleItemDone = () => { loadDailyTasks(); };
    window.addEventListener('study_plan_task_completed', handleTaskCompleted);
    window.addEventListener('study_plan_task_item_done', handleItemDone);
    return () => {
      window.removeEventListener('study_plan_task_completed', handleTaskCompleted);
      window.removeEventListener('study_plan_task_item_done', handleItemDone);
    };
  }, [dailyProgress, loadDailyTasks]);

  const handleToggleTask = async (taskId: string, currentStatus: boolean) => {
    if (!activePlan) return;
    const updated = await studyPlanService.toggleTaskComplete({
      userId,
      langCode: cleanLang,
      planDay: activePlan.current_day_index,
      taskId,
      completed: !currentStatus
    });
    if (updated) setDailyProgress(updated);
  };

  const handleTaskClick = async (task: PlanTask) => {
    // Missões de flashcard: salva o baralho da loja (se faltar) e abre direto dentro dele
    if (task.type === 'flashcards_words' || task.type === 'flashcards_phrases') {
      const level = (task.level || displayPlan?.current_level || 'A1').toUpperCase();
      const kind = task.type === 'flashcards_words' ? 'words' : 'phrases';
      const fallbackUrl = `/flashcards/guide/${cleanLang}/${level.toLowerCase()}?mode=${kind}&from=guide`;
      setIsImportingDeck(true);
      try {
        const { flashcardManager } = await import('../services/flashcardManager');
        const deckId = await flashcardManager.ensurePlanDeckImported({
          userId: userId || 'guest',
          targetLang: cleanLang,
          level,
          kind,
          nativeLang
        });
        navigate(deckId ? `${fallbackUrl}&deck=${encodeURIComponent(deckId)}` : fallbackUrl);
      } catch (e) {
        navigate(fallbackUrl);
      } finally {
        setIsImportingDeck(false);
      }
      return;
    }

    navigate(task.target_url);
  };

  const handleAdvanceNextDay = async () => {
    const planToAdvance = displayPlan || activePlan;
    if (!planToAdvance) return;
    const nextDay = planToAdvance.current_day_index + 1;
    const updatedPlan: StudyPlan = {
      ...planToAdvance,
      current_day_index: nextDay
    };
    await studyPlanService.savePlan(updatedPlan);

    // Gerar novas tarefas para o próximo dia
    const todayStr = getLocalISOString().split('T')[0];
    const newTasks = studyPlanService.generateTasksForDay({
      dayIndex: nextDay,
      level: planToAdvance.current_level,
      langCode: cleanLang,
      dailyMinutes: planToAdvance.daily_goal_minutes,
      pendingErrors
    });

    const newProg: DailyPlanProgress = {
      plan_day: nextDay,
      schedule_date: todayStr,
      tasks: newTasks,
      completed_tasks_count: 0,
      total_tasks_count: newTasks.length,
      minutes_spent: 0,
      status: 'pending'
    };

    await studyPlanService.saveDailyProgress(userId, cleanLang, newProg);
    setLocalPlan(updatedPlan);
    setDailyProgress(newProg);

    window.dispatchEvent(new CustomEvent('study_plan_updated', {
      detail: { lang: cleanLang, plan: updatedPlan }
    }));
  };

  const metrics = reportData?.metrics || {};
  const skillRadar = reportData?.skill_radar || { reading: 0, vocabulary: 0, active_voice: 0, writing: 0, pronunciation: 0, listening: 0 };

  const isTodayComplete = dailyProgress && dailyProgress.total_tasks_count > 0 && dailyProgress.completed_tasks_count >= dailyProgress.total_tasks_count;
  // Missão do dia completa: o "ta-dá" toca uma vez por dia
  useEffect(() => {
    if (!isTodayComplete) return;
    const key = `libritui_mission_sfx_${new Date().toISOString().slice(0, 10)}`;
    try { if (localStorage.getItem(key)) return; localStorage.setItem(key, '1'); } catch { return; }
    soundService.play('mission');
  }, [isTodayComplete]);

  // Janela de parabéns: aparece uma vez por dia do roteiro (fechar sem avançar não insiste)
  const dayDoneSeenKey = `libritui_day_done_seen_${userId || 'guest'}_${cleanLang}_${dailyProgress?.plan_day ?? 0}`;
  useEffect(() => {
    if (!isTodayComplete || isLoadingTasks || !displayPlan) return;
    if (dailyProgress?.plan_day !== displayPlan.current_day_index) return;
    try { if (localStorage.getItem(dayDoneSeenKey)) return; } catch {}
    setIsDayDoneModalOpen(true);
  }, [isTodayComplete, isLoadingTasks, dayDoneSeenKey, dailyProgress?.plan_day, displayPlan?.current_day_index]);

  const closeDayDoneModal = () => {
    try { localStorage.setItem(dayDoneSeenKey, '1'); } catch {}
    setIsDayDoneModalOpen(false);
  };

  const handleDayDoneAdvance = async () => {
    try { localStorage.setItem(dayDoneSeenKey, '1'); } catch {}
    setIsDayDoneModalOpen(false);
    await handleAdvanceNextDay();
    setOpenPanel('tasks');
  };

  // Sincronização manual: relê o plano e o progresso salvos, sem gerar missões novas para o dia
  const handleSyncMissions = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const freshPlan = (await studyPlanService.getPlan(userId, cleanLang)) || displayPlan;
      if (!freshPlan) return;
      if (freshPlan.current_day_index !== displayPlan?.current_day_index) {
        setLocalPlan(freshPlan);
        window.dispatchEvent(new CustomEvent('study_plan_updated', {
          detail: { lang: cleanLang, plan: freshPlan }
        }));
      }
      const prog = await loadDailyTasks(freshPlan);
      if (prog && prog.total_tasks_count > 0 && prog.completed_tasks_count >= prog.total_tasks_count) {
        setIsDayDoneModalOpen(true);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  // Resumo para os orbes (somente leitura dos dados já carregados)
  const SKILL_NAMES: Record<string, string> = { reading: 'Leitura', vocabulary: 'Vocabulário', active_voice: 'Conversação', writing: 'Escrita', pronunciation: 'Pronúncia', listening: 'Listening' };
  const skillEntries = Object.entries(skillRadar as Record<string, number>).filter(([k]) => k in SKILL_NAMES);
  const skillAvg = skillEntries.length ? skillEntries.reduce((s, [, v]) => s + (Number(v) || 0), 0) / skillEntries.length : 0;
  const topSkillEntry = skillEntries.slice().sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0))[0];
  const topSkill = topSkillEntry && Number(topSkillEntry[1]) > 0 ? SKILL_NAMES[topSkillEntry[0]] : '';
  const panelMotion = {
    initial: { opacity: 0, y: -10, height: 0 },
    animate: { opacity: 1, y: 0, height: 'auto' },
    exit: { opacity: 0, y: -10, height: 0 },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
    style: { overflow: 'hidden' as const },
  };
  const togglePanel = (p: HomePanel) => setOpenPanel(cur => (cur === p ? null : p));

  // Se o usuário ainda não tiver um plano ativo
  if (!activePlan) {
    return (
      <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 md:p-12 text-center space-y-6">
        <div className="w-20 h-20 bg-primary/10 text-primary rounded-3xl mx-auto flex items-center justify-center">
          <Sparkles size={40} />
        </div>
        <div className="max-w-md mx-auto space-y-2">
          <h2 className="text-2xl font-medium text-ink tracking-[-0.03em]">
            Comece Seu Plano de Estudos Personalizado
          </h2>
          <p className="text-xs text-muted font-medium leading-relaxed">
            Escolha seu idioma, tempo diário disponível e receba tarefas equilibradas de vocabulário, pronúncia com áudio, listening, escrita e gramática.
          </p>
        </div>
        <button
          onClick={onOpenNewPlanModal}
          className="px-8 py-4 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:scale-105 active:scale-95 transition-all cursor-pointer"
        >
          Criar Meu Plano de Estudos
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      
      {/* Top Language & Plan Selector */}
      <div className="flex items-center justify-between gap-3 bg-surface border border-black/[0.06] dark:border-white/[0.06] p-4 sm:p-5 rounded-[24px]">
        {/* Bandeira, idioma, nível, dia e meta */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
        <div className="relative">
          <button
            onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
            className="flex items-center gap-3 px-4 py-2.5 bg-transparent border border-black/[0.06] dark:border-white/[0.06] rounded-2xl text-ink font-medium text-sm hover:border-primary transition-all cursor-pointer"
          >
            <span className="text-2xl">{langMeta.flag}</span>
            <span>{langMeta.name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-primary/10 text-primary font-semibold">{activePlan.current_level}</span>
            <ChevronDown size={16} className="text-muted" />
          </button>

          {isLangDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-surface rounded-2xl border border-black/[0.06] dark:border-white/[0.06] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] p-2 z-50 animate-in zoom-in-95">
              {(userPlanLangs.length > 0 ? userPlanLangs : [cleanLang]).map(code => {
                const c = code.toLowerCase().split('-')[0];
                const info = LANGUAGE_CONFIG[c] || { name: c.toUpperCase(), flag: '🌐' };
                return (
                  <button
                    key={code}
                    onClick={() => {
                      onSelectLanguage(c);
                      setIsLangDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-xl text-xs font-bold transition-all ${
                      cleanLang === c ? 'bg-ink text-surface' : 'text-ink hover:bg-tint-stone/60 '
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span>{info.flag}</span>
                      <span>{info.name}</span>
                    </div>
                    {cleanLang === c && <Check size={16} />}
                  </button>
                );
              })}
              <div className="border-t border-black/[0.06] dark:border-white/[0.06] my-1 pt-1">
                <button
                  onClick={() => {
                    setIsLangDropdownOpen(false);
                    onOpenNewPlanModal();
                  }}
                  className="w-full flex items-center gap-2 p-3 text-xs font-semibold text-primary hover:bg-primary/10 rounded-xl transition-all"
                >
                  <Plus size={16} /> Novo Idioma / Plano
                </button>
              </div>
            </div>
          )}
        </div>

          {/* Status do Dia */}
          <div className="flex items-center gap-2 text-xs font-semibold text-muted">
            <span className="px-3 py-1.5 rounded-xl bg-tint-stone/60 text-ink whitespace-nowrap">
              DIA {displayPlan.current_day_index}
            </span>
            <span className="whitespace-nowrap">• Meta: {displayPlan.daily_goal_minutes} min/dia</span>
          </div>
        </div>

        {/* Ações do plano reunidas no menu de 3 pontinhos */}
        <div className="relative shrink-0" ref={moreMenuRef}>
          <button
            onClick={() => setIsMoreMenuOpen(o => !o)}
            className="p-2.5 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-primary hover:border-primary transition-all cursor-pointer"
            title="Mais opções"
            aria-label="Mais opções"
            aria-expanded={isMoreMenuOpen}
          >
            {isSyncing ? <RotateCw size={18} className="animate-spin" /> : <MoreHorizontal size={18} />}
          </button>

          {isMoreMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-60 bg-surface rounded-2xl border border-black/[0.06] dark:border-white/[0.06] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] p-2 z-50 animate-in zoom-in-95">
              <button
                onClick={() => { setIsMoreMenuOpen(false); handleSyncMissions(); }}
                disabled={isSyncing}
                className="w-full flex items-center gap-2.5 p-3 rounded-xl text-xs font-semibold text-ink hover:bg-tint-stone/60 transition-all cursor-pointer disabled:opacity-60"
              >
                <RotateCw size={15} className="text-primary" /> Sincronizar missões
              </button>
              <button
                onClick={() => { setIsMoreMenuOpen(false); onOpenRoadmapModal(); }}
                className="w-full flex items-center gap-2.5 p-3 rounded-xl text-xs font-semibold text-ink hover:bg-tint-stone/60 transition-all cursor-pointer"
              >
                <Calendar size={15} className="text-primary" /> Ver Roteiro
              </button>
              <button
                onClick={() => {
                  setIsMoreMenuOpen(false);
                  setCustomMinutes(displayPlan.daily_goal_minutes || 30);
                  setIsSettingsOpen(true);
                }}
                className="w-full flex items-center gap-2.5 p-3 rounded-xl text-xs font-semibold text-ink hover:bg-tint-stone/60 transition-all cursor-pointer"
              >
                <Settings size={15} className="text-primary" /> {i18nInstance.t('ui.plan.adjustTime')}
              </button>
              <div className="border-t border-black/[0.06] dark:border-white/[0.06] my-1 pt-1">
                <button
                  onClick={() => {
                    setIsMoreMenuOpen(false);
                    setDeleteConfirmationText('');
                    setIsDeleteModalOpen(true);
                  }}
                  className="w-full flex items-center gap-2.5 p-3 rounded-xl text-xs font-semibold text-bad hover:bg-tint-rose/60 transition-all cursor-pointer"
                >
                  <Trash2 size={15} /> Excluir rota de aprendizado
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Design minimal v1: visão geral em orbes (cada um abre a seção detalhada abaixo) */}
      <HomeOrbOverview
        open={openPanel}
        onToggle={togglePanel}
        tasksDone={dailyProgress?.completed_tasks_count || 0}
        tasksTotal={dailyProgress?.total_tasks_count || 0}
        minutes={metrics.equivalent_minutes || 0}
        words={metrics.words_learned || 0}
        consistency={metrics.consistency_percent || 0}
        skillAvg={skillAvg}
        topSkill={topSkill}
        pendingErrors={pendingErrors.length}
        masteredErrors={masteredErrors.length}
      />

      <div id="home-orb-panel" className="scroll-mt-24" />
      <AnimatePresence initial={false} mode="wait">
      {/* BLOCO 1: PENDÊNCIAS & TAREFAS DE HOJE */}
      {openPanel === 'tasks' && (
      <motion.div key="panel-tasks" {...panelMotion}>
      <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-black/[0.06] dark:border-white/[0.06] pb-4">
          <div>
            <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">CRONOGRAMA DO DIA</span>
            <h3 className="text-2xl font-medium tracking-[-0.03em] text-ink mt-0.5">
              Tarefas de Hoje {isTodayComplete ? '🎉 Concluídas!' : ''}
            </h3>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-muted">
            <span>Progresso:</span>
            <span className="text-primary font-semibold">
              {dailyProgress?.completed_tasks_count || 0} de {dailyProgress?.total_tasks_count || 0}
            </span>
          </div>
        </div>

        {/* Banner de Parabéns & Adiantar Próximo Dia */}
        {isTodayComplete && (
          <div className="p-5 rounded-2xl bg-gradient-to-r from-ok/10 via-ok/10 to-primary/10 border border-ok/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-in fade-in duration-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-ok text-surface flex items-center justify-center shrink-0">
                <Check size={22} strokeWidth={3} />
              </div>
              <div>
                <h4 className="text-sm font-medium text-ok dark:text-ok">
                  Parabéns! Todas as tarefas do Dia {displayPlan.current_day_index} foram concluídas!
                </h4>
                <p className="text-xs text-muted font-medium">
                  Você já atingiu sua meta diária de estudos. Se quiser continuar no ritmo, adiante o próximo dia!
                </p>
              </div>
            </div>
            <button
              onClick={handleAdvanceNextDay}
              className="px-5 py-2.5 bg-ok hover:bg-ok text-surface rounded-xl font-medium text-[13px] transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0"
            >
              <Sparkles size={15} /> Adiantar Dia {displayPlan.current_day_index + 1}
            </button>
          </div>
        )}

        {/* Lista de Tarefas */}
        {isLoadingTasks && !dailyProgress ? (
          <div className="py-12 px-4 rounded-2xl bg-tint-stone/50 border border-black/[0.06] dark:border-white/[0.06] text-center space-y-3 animate-pulse">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
            <p className="text-xs font-normal text-muted uppercase font-mono tracking-[0.14em]"><T k="ui.plan.loadingDay" /></p>
          </div>
        ) : (
          <div className="space-y-3">
            {dailyProgress?.tasks.map((task, idx) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(task)}
              className={`p-4 sm:p-5 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-4 group ${
                task.completed
                  ? 'border-ok/40 bg-ok/5 opacity-80'
                  : task.is_priority_error
                  ? 'border-bad/40 bg-bad/5 hover:border-bad'
                  : 'border-black/[0.06] dark:border-white/[0.06] hover:border-primary bg-tint-stone/40 '
              }`}
            >
              <div className="flex items-center gap-4 min-w-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleTask(task.id, task.completed);
                  }}
                  className={`w-7 h-7 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                    task.completed
                      ? 'bg-ok text-surface'
                      : 'border border-black/[0.06] dark:border-white/[0.06] hover:border-primary'
                  }`}
                >
                  {task.completed && <Check size={16} strokeWidth={3} />}
                </button>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className={`text-sm font-medium truncate ${
                      task.completed ? 'line-through text-muted' : 'text-ink group-hover:text-primary transition-colors'
                    }`}>
                      {task.title}
                    </h4>
                    {task.is_priority_error && (
                      <span className="px-2 py-0.5 rounded-md bg-bad/10 text-bad text-[9px] font-medium">
                        Refazer Erros
                      </span>
                    )}
                    {/* Badge de Progresso Parcial (se começou mas não completou) */}
                    {!task.completed && task.current_progress && task.current_progress > 0 ? (
                      <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-semibold font-mono">
                        {task.target_count 
                          ? `${task.current_progress}/${task.target_count} itens`
                          : `${Math.floor(task.current_progress / 60)}:${(task.current_progress % 60) < 10 ? '0' : ''}${task.current_progress % 60}/${task.estimated_minutes}:00 min`}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted font-medium truncate mt-0.5">{task.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs font-semibold text-muted">{task.estimated_minutes} min</span>
                <div className="p-2 rounded-xl bg-surface text-muted group-hover:text-primary group-hover:translate-x-1 transition-all">
                  <ChevronRight size={16} />
                </div>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Ação Conclusão / Adiantar Amanhã */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted font-medium">
            💡 Concluiu as tarefas de hoje? Você pode adiantar o dia seguinte ou continuar praticando livremente.
          </p>

          {isTodayComplete && (
            <button
              onClick={handleAdvanceNextDay}
              className="w-full sm:w-auto px-6 py-3.5 bg-ok hover:bg-ok text-surface rounded-2xl font-medium text-[13px] transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Sparkles size={16} /> Adiantar Tarefas do Dia {displayPlan.current_day_index + 1}
            </button>
          )}
        </div>
      </div>
      </motion.div>
      )}

      {/* BLOCO 2: RESUMO DE ATIVIDADES (Hoje, Semana, Mês, Total) */}
      {openPanel === 'summary' && (
      <motion.div key="panel-summary" {...panelMotion}>
      <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary"><T k="ui.plan.metrics" /></span>
            <h3 className="text-2xl font-medium tracking-[-0.03em] text-ink mt-0.5">
              Resumo de Atividades
            </h3>
          </div>

          {/* Period switcher */}
          <div className="flex items-center bg-tint-stone/60 p-1.5 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
            {(['daily', 'weekly', 'monthly', 'yearly'] as const).map(p => (
              <button
                key={p}
                onClick={() => setSummaryPeriod(p)}
                className={`px-3 py-1.5 rounded-xl text-xs font-normal uppercase font-mono tracking-[0.14em] transition-all cursor-pointer ${
                  summaryPeriod === p 
                    ? 'bg-ink text-surface' 
                    : 'text-muted hover:text-ink'
                }`}
              >
                {p === 'daily' ? 'Hoje' : p === 'weekly' ? 'Semana' : p === 'monthly' ? 'Mês' : 'Total'}
              </button>
            ))}
          </div>
        </div>

        {/* 4 Cards de Resumo */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] space-y-1">
            <span className="text-[10px] font-normal uppercase font-mono text-muted tracking-[0.14em]"><T k="ui.plan.vocab" /></span>
            <h4 className="text-2xl font-medium text-ink">{metrics.words_learned || 0}</h4>
            <p className="text-[11px] text-muted font-bold">palavras assimiladas</p>
          </div>

          <div className="p-4 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] space-y-1">
            <span className="text-[10px] font-normal uppercase font-mono text-muted tracking-[0.14em]">TEMPO DE ESTUDO</span>
            <h4 className="text-2xl font-medium text-ink">{metrics.equivalent_minutes || 0}m</h4>
            <p className="text-[11px] text-muted font-bold">tempo ativo dedicado</p>
          </div>

          <div className="p-4 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] space-y-1">
            <span className="text-[10px] font-normal uppercase font-mono text-muted tracking-[0.14em]"><T k="ui.plan.accuracy" /></span>
            <h4 className="text-2xl font-medium text-ok">{metrics.accuracy_rate || 0}%</h4>
            <p className="text-[11px] text-muted font-bold">acertos nos testes</p>
          </div>

          <div className="p-4 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] space-y-1">
            <span className="text-[10px] font-normal uppercase font-mono text-muted tracking-[0.14em]"><T k="ui.plan.consistency" /></span>
            <h4 className="text-2xl font-medium text-warn">{metrics.consistency_percent || 0}%</h4>
            <p className="text-[11px] text-muted font-bold">dias de estudo no ciclo</p>
          </div>
        </div>
      </div>
      </motion.div>
      )}

      {/* BLOCO 3: EQUILÍBRIO DE HABILIDADES & CENTRAL DE ERROS (um de cada vez, conforme o orbe) */}
      {(openPanel === 'skills' || openPanel === 'errors') && (
      <motion.div key={`panel-${openPanel}`} {...panelMotion}>

        {/* Equilíbrio de Habilidades (Idêntico ao da Imagem 1) */}
        {openPanel === 'skills' && (
        <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 sm:p-8 space-y-6">
          <div>
            <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">PERFIL DO ESTUDANTE</span>
            <h3 className="text-2xl font-medium tracking-[-0.03em] text-ink mt-1">
              Equilíbrio de Habilidades
            </h3>
            <p className="text-xs font-bold text-muted mt-1"><T k="ui.plan.metricsHint" /></p>
          </div>

          <div className="space-y-4 pt-2">
            {[
              { name: 'LEITURA DE LIVROS', pct: skillRadar.reading || 0, color: 'bg-tintd-lavender' },
              { name: 'VOCABULÁRIO & FLASHCARDS', pct: skillRadar.vocabulary || 0, color: 'bg-tintd-sky' },
              { name: 'CONVERSAÇÃO (VOZ ATIVA)', pct: skillRadar.active_voice || 0, color: 'bg-ok' },
              { name: 'PRÁTICA DE ESCRITA', pct: skillRadar.writing || 0, color: 'bg-tintd-rose' },
              { name: 'PRONÚNCIA FONÉTICA', pct: skillRadar.pronunciation || 0, color: 'bg-tintd-sky' },
              { name: 'COMPREENSÃO AUDITIVA (LISTENING)', pct: skillRadar.listening || 0, color: 'bg-tintd-lavender' }
            ].map(s => (
              <div key={s.name} className="space-y-1.5">
                <div className="flex justify-between text-xs font-normal uppercase font-mono tracking-[0.14em]">
                  <span className="text-ink">{s.name}</span>
                  <span className="text-muted">{s.pct}%</span>
                </div>
                <div className="w-full h-3 bg-tint-stone/60 rounded-full overflow-hidden">
                  <div className={`h-full ${s.color} rounded-full transition-all duration-700`} style={{ width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Central de Erros & Maestria */}
        {openPanel === 'errors' && (
        <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 sm:p-8 space-y-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-warn"><T k="ui.plan.exerciseHub" /></span>
                <h3 className="text-2xl font-medium tracking-[-0.03em] text-ink mt-1">Erros & Maestria</h3>
              </div>
              <div className="p-3 bg-warn/10 text-warn rounded-2xl">
                <Award size={24} />
              </div>
            </div>

            {/* Tab Switcher: Superados vs Pendentes */}
            <div className="flex items-center gap-2 mt-4 bg-tint-stone/60 p-1.5 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
              <button
                onClick={() => setErrorTab('pending')}
                className={`flex-1 py-2 text-xs font-normal uppercase font-mono tracking-[0.14em] rounded-xl transition-all cursor-pointer ${
                  errorTab === 'pending' ? 'bg-bad text-surface' : 'text-muted hover:text-ink'
                }`}
              >
                ⚠️ Pendentes ({pendingErrors.length})
              </button>
              <button
                onClick={() => setErrorTab('mastered')}
                className={`flex-1 py-2 text-xs font-normal uppercase font-mono tracking-[0.14em] rounded-xl transition-all cursor-pointer ${
                  errorTab === 'mastered' ? 'bg-warn text-ink' : 'text-muted hover:text-ink'
                }`}
              >
                Superados ({masteredErrors.length})
              </button>
            </div>
          </div>

          {/* Lista de Erros Agrupados por Tipo */}
          <div className="space-y-3 my-4 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
            {errorTab === 'pending' ? (
              pendingErrors.length === 0 ? (
                <div className="p-8 text-center bg-transparent rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
                  <CheckCircle2 size={32} className="mx-auto text-ok mb-2" />
                  <p className="text-xs font-bold text-muted"><T k="ui.plan.noPending" /></p>
                </div>
              ) : (
                (() => {
                  const TYPE_LABELS: Record<string, { label: string; icon: string }> = {
                    quizzes: { label: 'Quizzes', icon: '❓' },
                    flashcards: { label: 'Flashcards', icon: '📚' },
                    pronunciation: { label: 'Pronúncia', icon: '🎙️' },
                    writing: { label: 'Escrita', icon: '✍️' },
                    listening: { label: 'Compreensão Auditiva', icon: '🎧' }
                  };
                  const groupedMap: Record<string, number> = {};
                  pendingErrors.forEach((item: any) => {
                    const rawType = (item.exercise_type || 'outros').toLowerCase();
                    const typeKey = rawType.includes('quiz') ? 'quizzes' :
                                    rawType.includes('flashcard') ? 'flashcards' :
                                    rawType.includes('pronun') ? 'pronunciation' :
                                    rawType.includes('writ') || rawType.includes('escrita') ? 'writing' :
                                    rawType.includes('listen') ? 'listening' : rawType;
                    groupedMap[typeKey] = (groupedMap[typeKey] || 0) + 1;
                  });

                  return Object.entries(groupedMap).map(([typeKey, count]) => {
                    const meta = TYPE_LABELS[typeKey] || { label: typeKey.toUpperCase(), icon: '📝' };
                    return (
                      <div
                        key={typeKey}
                        className="p-4 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between gap-3 hover:border-black/[0.06] transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{meta.icon}</span>
                          <div>
                            <span className="font-semibold text-sm text-ink block">
                              {count} {meta.label}
                            </span>
                            <span className="text-[10px] text-bad font-normal uppercase font-mono tracking-[0.14em]">
                              Exercícios pendentes para refazer
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/review-errors/guide?lang=${cleanLang}&type=${typeKey}&from=guide`)}
                          className="px-4 py-2 rounded-xl bg-primary hover:bg-ok text-slate-900 text-[11px] font-medium hover:scale-105 active:scale-95 transition-all cursor-pointer"
                        >
                          Refazer
                        </button>
                      </div>
                    );
                  });
                })()
              )
            ) : (
              masteredErrors.length === 0 ? (
                <div className="p-8 text-center bg-transparent rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
                  <p className="text-xs font-bold text-muted">Nenhum item superado ainda.</p>
                </div>
              ) : (
                (() => {
                  const TYPE_LABELS: Record<string, { label: string; icon: string }> = {
                    quizzes: { label: 'Quizzes', icon: '❓' },
                    flashcards: { label: 'Flashcards', icon: '📚' },
                    pronunciation: { label: 'Pronúncia', icon: '🎙️' },
                    writing: { label: 'Escrita', icon: '✍️' },
                    listening: { label: 'Compreensão Auditiva', icon: '🎧' }
                  };
                  const groupedMap: Record<string, number> = {};
                  masteredErrors.forEach((item: any) => {
                    const rawType = (item.exercise_type || 'outros').toLowerCase();
                    const typeKey = rawType.includes('quiz') ? 'quizzes' :
                                    rawType.includes('flashcard') ? 'flashcards' :
                                    rawType.includes('pronun') ? 'pronunciation' :
                                    rawType.includes('writ') || rawType.includes('escrita') ? 'writing' :
                                    rawType.includes('listen') ? 'listening' : rawType;
                    groupedMap[typeKey] = (groupedMap[typeKey] || 0) + 1;
                  });

                  return Object.entries(groupedMap).map(([typeKey, count]) => {
                    const meta = TYPE_LABELS[typeKey] || { label: typeKey.toUpperCase(), icon: '📝' };
                    return (
                      <div
                        key={typeKey}
                        className="p-4 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{meta.icon}</span>
                          <div>
                            <span className="font-semibold text-sm text-ink block">
                              {count} {meta.label} Superados
                            </span>
                            <span className="text-[10px] text-ok font-normal uppercase font-mono tracking-[0.14em]">
                              Maestria atingida
                            </span>
                          </div>
                        </div>
                        <CheckCircle2 size={20} className="text-ok mr-2" />
                      </div>
                    );
                  });
                })()
              )
            )}
          </div>

          <button
            onClick={() => navigate(`/review-errors/guide?lang=${cleanLang}&from=guide`)}
            className="text-[13px] font-medium w-full h-12 bg-tint-stone/60 hover:bg-tint-stone text-ink rounded-full transition-all cursor-pointer text-center"
          >
            Abrir Caderno de Erros Completo
          </button>
        </div>
        )}

      </motion.div>
      )}
      </AnimatePresence>

      {/* Modal de Confirmação de Importação de Baralho */}
      {deckImportNotice?.isOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 max-w-md w-full space-y-6 text-center animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-3xl mx-auto flex items-center justify-center">
              <Sparkles size={32} />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-ink tracking-[-0.03em]">
                {deckImportNotice.title}
              </h3>
              <p className="text-xs text-muted font-medium leading-relaxed">
                {deckImportNotice.message}
              </p>
            </div>
            <button
              onClick={() => {
                const target = deckImportNotice.targetUrl;
                setDeckImportNotice(null);
                navigate(target);
              }}
              className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            >
              Confirmar e Começar
            </button>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIGURAÇÃO DE TEMPO DIÁRIO */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 sm:p-8 max-w-md w-full space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <Settings size={24} />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-ink">
                    Tempo Diário de Estudo
                  </h3>
                  <p className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">
                    {langMeta.flag} {langMeta.name} • Nível {displayPlan?.current_level}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="p-2 rounded-xl text-muted hover:text-muted hover:bg-tint-stone/60 transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-tint-stone/40 border border-black/[0.06]">
              <p className="text-xs text-muted leading-relaxed font-medium">
                Escolha quantos minutos por dia você deseja estudar. As tarefas que ainda estão <strong>em aberto hoje serão recalculadas</strong> proporcionalmente. Suas tarefas já concluídas <strong>não serão alteradas</strong>.
              </p>
            </div>

            {/* Opções pré-definidas */}
            <div className="space-y-2">
              <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted block px-1">
                SELECIONE A META DE TEMPO:
              </span>
              <div className="grid grid-cols-3 gap-2.5">
                {[15, 30, 45, 60, 90, 120].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => setCustomMinutes(mins)}
                    className={`py-3 px-2 rounded-2xl font-semibold text-xs transition-all cursor-pointer border flex flex-col items-center gap-0.5 ${
                      customMinutes === mins
                        ? 'bg-ink text-surface border-primary scale-[1.02]'
                        : 'bg-transparent text-ink border-black/[0.06] dark:border-white/[0.06] hover:border-primary/80'
                    }`}
                  >
                    <span className="text-base font-semibold">{mins}</span>
                    <span className="text-[9px] uppercase font-mono tracking-[0.14em] opacity-80">minutos</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsSettingsOpen(false)}
                disabled={isSavingSettings}
                className="flex-1 py-3.5 px-4 rounded-2xl font-medium text-[13px] text-muted hover:text-ink hover:bg-tint-stone/60 transition-all cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveDailyMinutes}
                disabled={isSavingSettings}
                className="flex-1 py-3.5 px-4 bg-primary text-slate-900 rounded-2xl font-medium text-[13px] hover:opacity-90 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                {isSavingSettings ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <span><T k="ui.common.saveChanges" /></span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO PARA EXCLUIR ROTA DE APRENDIZADO */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 sm:p-8 max-w-md w-full space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-bad/10 text-bad flex items-center justify-center">
                  <Trash2 size={24} />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-ink leading-tight">
                    {deleteModalI18n.title}
                  </h3>
                  <p className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em] mt-0.5">
                    {langMeta.flag} {langMeta.name} • {displayPlan?.current_level}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (!isDeletingPlan) {
                    setIsDeleteModalOpen(false);
                    setDeleteConfirmationText('');
                  }
                }}
                disabled={isDeletingPlan}
                className="p-2 rounded-xl text-muted hover:text-muted hover:bg-tint-stone/60 transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-tint-rose/70 dark:bg-tint-rose/30 border border-tint-rose/80 dark:border-tint-rose/40">
              <p className="text-xs text-bad dark:text-bad leading-relaxed font-medium">
                {deleteModalI18n.warning}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] font-bold text-muted block">
                {deleteModalI18n.instruction}
              </label>
              <input
                type="text"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                placeholder={deleteModalI18n.placeholder}
                disabled={isDeletingPlan}
                autoFocus
                className="w-full px-4 py-3 rounded-2xl bg-transparent border border-black/[0.06] dark:border-white/[0.06] text-sm font-bold text-ink focus:outline-none focus:border-bad transition-colors placeholder:text-muted"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteConfirmationText('');
                }}
                disabled={isDeletingPlan}
                className="flex-1 py-3.5 px-4 rounded-2xl font-medium text-[13px] text-muted hover:text-ink hover:bg-tint-stone/60 transition-all cursor-pointer"
              >
                {deleteModalI18n.cancel}
              </button>
              <button
                type="button"
                onClick={handleDeletePlan}
                disabled={isDeletingPlan || deleteConfirmationText.trim().toLowerCase() !== 'delete'}
                className={`text-[13px] font-medium flex-1 h-12 px-4 rounded-full transition-all flex items-center justify-center gap-2 ${ deleteConfirmationText.trim().toLowerCase() === 'delete' && !isDeletingPlan ? 'bg-bad hover:bg-bad text-white cursor-pointer active:scale-95' : 'bg-tint-stone text-muted cursor-not-allowed opacity-70' }`}
              >
                {isDeletingPlan ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>{deleteModalI18n.deleting}</span>
                  </>
                ) : (
                  <span>{deleteModalI18n.confirm}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* JANELA DE PARABÉNS: TODAS AS MISSÕES DO DIA CONCLUÍDAS */}
      {isDayDoneModalOpen && displayPlan && (
        <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="relative bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-6 sm:p-8 max-w-sm w-full text-center space-y-5 animate-in zoom-in-95 duration-200">
            <button
              onClick={closeDayDoneModal}
              className="absolute top-4 right-4 p-2 rounded-xl text-muted hover:text-ink hover:bg-tint-stone/60 transition-colors cursor-pointer"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>

            <img
              src="/icons/icon-192.png"
              alt="LibriTui"
              className="w-20 h-20 rounded-[22px] mx-auto shadow-[0_18px_40px_-18px_rgba(0,0,0,0.45)]"
            />

            <div className="space-y-2">
              <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">
                {langMeta.flag} {langMeta.name} • {displayPlan.current_level}
              </span>
              <h3 className="text-2xl font-medium tracking-[-0.03em] text-ink">
                Parabéns! 🎉
              </h3>
              <p className="text-xs text-muted font-medium leading-relaxed">
                Você concluiu todas as missões do Dia {displayPlan.current_day_index}. Bora para o próximo nível do roteiro?
              </p>
            </div>

            <div className="space-y-2 pt-1">
              <button
                onClick={handleDayDoneAdvance}
                className="w-full h-12 px-5 bg-ok hover:bg-ok text-surface rounded-full font-medium text-[13px] transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
              >
                <Sparkles size={16} /> Ir para o Dia {displayPlan.current_day_index + 1}
              </button>
              <button
                onClick={closeDayDoneModal}
                className="w-full py-2.5 rounded-full font-medium text-[12px] text-muted hover:text-ink transition-colors cursor-pointer"
              >
                Agora não
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
